import { sha256 } from "sha.js";

import { SideCaddyActions } from "../actions/SideCaddyActions";
import {
	AnonCartNotificationStore,
	CartNotificationStoreImpl,
	CustomerCartNotificationStore,
} from "./CartNotificationStore";
import { CartItemStoreImpl, CustomerCartItemStore } from "./common-cart/CartItemStore";
import { CartContextStoreImpl, CustomerCartContextStore } from "./common-cart/CartContextStore";
import { CheckoutAggregatorStore, CheckoutAggregatorStoreImpl } from "./checkout/CheckoutAggregatorStore";
import { AnonCartStore, AnonCartStoreImpl } from "./anon-cart/AnonCartStore";
import { CustomerCartStore, CustomerCartStoreImpl } from "./customer-cart/CustomerCartStore";
import { Store } from "../utils/Store";
import { AnonCartActions } from "../actions/AnonCartActions";
import { CustomerCartActions } from "../actions/CustomerCartActions";
import { SavedCartActions } from "../actions/SavedCartActions";
import { OrderEntryActions } from "../actions/OrderEntryActions";
import { CartContextActions } from "../actions/CartContextActions";
import { CartSearchActions } from "../actions/CartSearchActions";
import { CustomerSearchActions } from "../actions/CustomerSearchActions";
import { QuickEntryActions } from "../actions/QuickEntryActions";
import { TrackingActions } from "../actions/TrackingActions";
import { SummaryPageActions } from "../actions/SummaryPageActions";
import { OrderOptionsPanelActions } from "../actions/OrderOptionsPanelActions";
import {
	getSubmitSuccessTrackingTags,
	trackStructured,
	trackStructuredEvent,
	transformDnrInfoArrayToConcattedString,
} from "../utils/TrackingUtil";
import { CartRecommendationActions } from "../actions/CartRecommendationActions";
import { SurveyActions } from "../actions/SurveyActions";
import { CSVUploadActions } from "../actions/CSVUploadActions";
import { CustomerCartPageActions } from "../actions/CustomerCartPageActions";
import { getMetroCredit } from "../utils/CheckoutUtil";
import { filterItems } from "./FilterCartItems";
import { PaymentSelectorActions } from "../actions/PaymentSelectorActions";
import { DatePickerActions } from "../actions/DatePickerActions";
import { CheckoutSteps } from "../model/CheckoutSteps";
import { TrackingStoreGAUtils } from "./TrackingStoreGAUtils";
import { formatLocalDate, formatLocalTime, getTimezoneOffset } from "../utils/DateUtil";

import type {
	CheckoutData,
	PaymentId,
	PaymentMethodSelectionDetails,
	PaymentType,
	Timeslot,
	UnavailabilityReason,
} from "../model/CheckoutData";
import type { ErrorState } from "./CartNotificationStore";
import type { SurveyTrackingData } from "./SurveyStore";
import type { AnonCart } from "../model/AnonCartTypes";
import type { Money } from "./BaseTypes";
import type { ArticleData } from "../model/ArticleTypes";
import type { SearchResultEntry } from "../model/ArticleSearchTypes";
import type { IntlNotification } from "../utils/notifications";
import type { SearchResults } from "./order-entry/QuickEntryStore";
import type { PaymentPreauthorizationRequestData } from "./CheckoutTypes";
import type { PredictionWithArticleData } from "./customer-cart/CartRecommendationStore";
import type { AddItemParams } from "./common-cart/CommonCartStore";
import type { ItemEntry } from "./order-entry/CSVUploadStore";
import type { SessionContext } from "../model/CustomerCartTypes";
import type { ValidDeliveryDate, ValidPickupDate } from "../server/StoreInfoRequests";

export class TrackingStoreImpl extends Store {
	_trackingBuffer:
		| {
				cartId: string;
				bundleIds: Array<string>;
				trackingFunction: (
					cartId: string | null | undefined,
					bundleIds: string[],
					articles: ArticleData[],
				) => void;
		  }
		| null
		| undefined;
	_customerCartItemStore: CartItemStoreImpl<SessionContext>;
	_anonCartNotificationStore: CartNotificationStoreImpl;
	_customerCartNotificationStore: CartNotificationStoreImpl;
	_anonCartStore: AnonCartStoreImpl;
	_customerCartStore: CustomerCartStoreImpl;
	_customerCartContextStore: CartContextStoreImpl;
	_checkoutAggregatorStore: CheckoutAggregatorStoreImpl;

	constructor(
		customerCartItemStore: CartItemStoreImpl<SessionContext>,
		anonCartNotificationStore: CartNotificationStoreImpl,
		customerCartNotificationStore: CartNotificationStoreImpl,
		anonCartStore: AnonCartStoreImpl,
		customerCartStore: CustomerCartStoreImpl,
		customerCartContextStore: CartContextStoreImpl,
		checkoutAggregatorStore: CheckoutAggregatorStoreImpl,
	) {
		super();
		this._customerCartItemStore = customerCartItemStore;
		this._anonCartNotificationStore = anonCartNotificationStore;
		this._customerCartNotificationStore = customerCartNotificationStore;
		this._anonCartStore = anonCartStore;
		this._customerCartStore = customerCartStore;
		this._customerCartContextStore = customerCartContextStore;
		this._checkoutAggregatorStore = checkoutAggregatorStore;

		this._initTracking();
	}

	_initTracking() {
		this.trackAnonCart();
		this.trackCustomerCart();
		this.trackQuickEntry();
		this.trackSurvey();
		this.trackCheckoutOptions();
		this.trackCheckout();
		this.trackDateChanges();
		this.trackOrderEntry();
		this.trackCartRecommendations();
		this.trackCSVUpload();
		this.trackSideCaddy();
		this.trackSavedCart();
		this.trackAbandonedCart();
		this.listenTo(this._customerCartItemStore, this.handleCartItemStoreUpdated);
		this.handleTrackingActions();
	}

	static hashedValue(value: string) {
		return new sha256().update(value).digest("hex");
	}

	handleTrackingActions() {
		/* TODO please replace this with proper actions, that actually mean something */
		this.listenTo(TrackingActions.trackCheckoutStep, this.trackCheckoutStep);
		this.listenTo(TrackingActions.trackCheckoutNavigation, this.trackCheckoutNavigation);
		this.listenTo(TrackingActions.trackCheckoutCreated, this.trackCheckoutCreated);
		this.listenTo(TrackingActions.onPaymentPreauthorization, this.trackPaymentPreauthorization);
		this.listenTo(TrackingActions.trackSubmitSuccess, this.trackSubmitSuccess.bind(this, {}));
		this.listenTo(TrackingActions.trackCreditCheck, TrackingStoreImpl.trackCreditCheck);
		this.listenTo(TrackingActions.trackMovWarningShown, TrackingStoreImpl.trackMovShown);
		this.listenTo(TrackingActions.trackNotificationShown, TrackingStoreImpl.trackNotificationShown);
		this.listenTo(TrackingActions.trackCartRestocked, this.trackCartRestocked);
		this.listenTo(TrackingActions.trackSavedCartLoaded, this.trackSavedCartLoaded);
		this.listenTo(TrackingActions.trackUnavailablePaymentShown, this.trackUnavailablePaymentShown);
		this.listenTo(TrackingActions.trackUnavailableMetroCreditShown, this.trackUnavailableMetroCreditShown);
		this.listenTo(TrackingActions.trackCheckoutSkipStep, this.trackCheckoutSkipStep);
		this.listenTo(TrackingActions.trackGuerillaFeature, TrackingStoreImpl.trackGuerillaFeature);
		this.listenTo(TrackingActions.trackComponent, this.trackComponent);
	}

	handleCartItemStoreUpdated() {
		const trackingBuffer = this._trackingBuffer;

		if (!trackingBuffer) {
			return;
		}

		const fetchedArticleData: ArticleData[] = [...this._customerCartItemStore.articleData]
			.filter(([key]) => trackingBuffer.bundleIds.includes(key))
			.map(([, article]) => article);
		const fetchedArticleDataBundles = [...this._customerCartItemStore.articleData.keys()];
		const missingData = trackingBuffer.bundleIds.filter((entry) => !fetchedArticleDataBundles.includes(entry));

		if (missingData.length !== 0) {
			return;
		}

		trackingBuffer.trackingFunction(trackingBuffer.cartId, trackingBuffer.bundleIds, fetchedArticleData);
		this._trackingBuffer = null;
		this.trigger();
	}

	trackCartRestocked(cartId: string, bundleIds: string[]) {
		this._trackingBuffer = {
			cartId,
			bundleIds,
			trackingFunction: TrackingStoreImpl.trackCartRestockedSuccess,
		};
	}

	trackSavedCartLoaded(cartId: string, bundleIds: string[]) {
		this._trackingBuffer = {
			cartId,
			bundleIds,
			trackingFunction: TrackingStoreImpl.trackSavedCartLoadedSuccess,
		};
	}

	trackDateChanges() {
		this.listenTo(DatePickerActions.setCartDeliveryDate, (date: Date, info: ValidDeliveryDate) =>
			TrackingStoreImpl.trackDeliveryDate(date, info, "cart"),
		);
		this.listenTo(DatePickerActions.setCheckoutDeliveryDate, (date: Date, info: ValidDeliveryDate) =>
			TrackingStoreImpl.trackDeliveryDate(date, info, "checkout"),
		);
		this.listenTo(
			DatePickerActions.setPickupDate,
			(date: Date, info: ValidPickupDate, timeslot: Timeslot | null | undefined) =>
				TrackingStoreImpl.trackPickupDate(date, info, timeslot),
		);
	}

	trackAnonCart() {
		this.listenTo(this._anonCartNotificationStore, () => {
			const errorState = this._anonCartNotificationStore.getErrorState();

			this.trackCartErrors(errorState);
		});
		this.listenTo(AnonCartActions.deleteCart, this.trackCartDeleted.bind(this));
		this.listenTo(AnonCartActions.updateItemsFilter, this.trackCartFilterChanged.bind(this));
		this.listenTo(AnonCartActions.changeSortCriterion, this.trackSortCriterionChanged.bind(this));
		this.listenTo(AnonCartActions.addItem, this.trackItemAdded.bind(this, {}));
		this.listenTo(AnonCartActions.setItemQuantity, this.trackItemQuantitySet.bind(this));
		this.listenTo(AnonCartActions.markCartMerged, this.trackCartMerged.bind(this));
	}

	trackOrderEntry() {
		this.listenTo(OrderEntryActions.deleteSpecificCart, this.trackCartDeleted.bind(this));
		this.listenTo(
			CartSearchActions.onCustomerSelected,
			TrackingStoreImpl.trackCartsPageCustomerSelected.bind(this),
		);
		this.listenTo(CartContextActions.onOpenOrderEntry, TrackingStoreImpl.trackOrderEntryOpen.bind(this));
		this.listenTo(CartContextActions.onCloseOrderEntry, TrackingStoreImpl.trackOrderEntryClose.bind(this));
		this.listenTo(CustomerSearchActions.onCartCreated, TrackingStoreImpl.trackFOECartCreated.bind(this));
		this.listenTo(OrderEntryActions.onSubmit, TrackingStoreImpl.trackFOECartSubmitted.bind(this));
		this.listenTo(
			CustomerSearchActions.onCustomerSelected,
			TrackingStoreImpl.trackCreateCartPageCustomerSelected.bind(this),
		);
	}

	trackAbandonedCart() {
		this.listenTo(TrackingActions.trackAbandonedCartEmailClick, this.trackAbandonedCartEmailClick.bind(this));
		this.listenTo(
			TrackingActions.trackAbandonedCartEmailCartMismatch,
			this.trackAbandonedCartEmailCartMismatch.bind(this),
		);
	}

	trackSavedCart() {
		this.listenTo(CartContextActions.onOpenSavedCart, this.trackEditSavedCart.bind(this));
		this.listenTo(SavedCartActions.deleteCart, this.trackDeleteSavedCart.bind(this));
	}

	trackCustomerCart() {
		this.listenTo(this._customerCartNotificationStore, () => {
			const errorState = this._customerCartNotificationStore.getErrorState();

			this.trackCartErrors(errorState);
		});
		this.listenTo(CustomerCartActions.deleteCart, this.trackCartDeleted.bind(this));
		this.listenTo(CustomerCartActions.saveCart, this.trackCartSaved.bind(this));
		this.listenTo(CustomerCartActions.addItem, this.trackItemAdded.bind(this, {}));
		this.listenTo(CustomerCartActions.setItemQuantity, this.trackItemQuantitySet.bind(this));
		this.listenTo(CustomerCartActions.setItemServiceOption, this.trackItemServiceOptionSet.bind(this));
		this.listenTo(CustomerCartActions.setItemComment, this.trackItemCommentSet.bind(this));
		this.listenTo(CustomerCartActions.onSetManualPrice, this.trackManualPriceSet.bind(this));
		this.listenTo(CustomerCartPageActions.printCart, this.trackCartPrinted.bind(this));
		this.listenTo(CustomerCartPageActions.updateItemsFilter, this.trackCartFilterChanged.bind(this));
		this.listenTo(CustomerCartPageActions.changeSortCriterion, this.trackSortCriterionChanged.bind(this));
		this.listenTo(CustomerCartPageActions.checkOut, this.trackCheckoutCart.bind(this, false));
		this.listenTo(CustomerCartPageActions.oneClickSubmit, this.trackCheckoutCart.bind(this, true));
		this.listenTo(CustomerCartActions.onRestockCart, this.trackRestockCart.bind(this));
		this.listenTo(CustomerCartActions.onRemoveCartFromContext, this.trackResetCart.bind(this));
		this.listenTo(CustomerCartActions.onLoadSavedCart, this.trackLoadSavedCart.bind(this));
		this.listenTo(CustomerCartActions.replaceArticle, this.trackReplaceArticle.bind(this));
	}

	trackCSVUpload() {
		this.listenTo(CSVUploadActions.showModal, this.trackUploadModalShow.bind(this));
		this.listenTo(CSVUploadActions.clear, this.trackUploadModalClear.bind(this));
		this.listenTo(CSVUploadActions.uploadFile, this.trackUploadCsvFile.bind(this));
		this.listenTo(CSVUploadActions.pasteCSV, this.trackPasteCsv.bind(this));
		this.listenTo(CSVUploadActions.deleteItem, this.trackDeleteCsvItem.bind(this));
		this.listenTo(CSVUploadActions.setComment, this.trackCsvCommentChange.bind(this));
		this.listenTo(CSVUploadActions.setQuantity, this.trackCsvQuantityChange.bind(this));
		this.listenTo(CSVUploadActions.addToCart, this.trackUploadModalAddToCart.bind(this));
	}

	trackSurvey() {
		this.listenTo(SurveyActions.onNewSurvey, this.trackSurveyStart.bind(this));
		this.listenTo(SurveyActions.onSetRating, this.trackRatingClick.bind(this));
		this.listenTo(SurveyActions.onSubmitSurvey, this.trackSurveySubmit.bind(this));
	}

	trackCheckoutOptions() {
		this.listenTo(OrderOptionsPanelActions.onSetSingleInvoice, TrackingStoreImpl.trackSingleInvoiceSet.bind(this));
	}

	trackCheckout() {
		this.listenTo(PaymentSelectorActions.ChangeMainPaymentMethod, this.trackPaymentMethodChange.bind(this, true));
		this.listenTo(
			PaymentSelectorActions.ChangeComplementaryPaymentMethod,
			this.trackPaymentMethodChange.bind(this, false),
		);
		this.listenTo(
			PaymentSelectorActions.ShowUnavailabilityDialogMetroCreditBlocked,
			this.trackShowUnavailabilityDialogMetroCreditBlocked.bind(this),
		);
		this.listenTo(
			PaymentSelectorActions.CancelUnavailabilityDialog,
			this.trackCancelUnavailabilityDialog.bind(this),
		);
		this.listenTo(SummaryPageActions.onSubmitCheckout, this.trackSubmitCheckoutAttempt.bind(this, false));
	}

	trackQuickEntry() {
		const tags = {
			quickEntry: "true",
		};
		this.listenTo(QuickEntryActions.showMoreResults, TrackingStoreImpl.trackShowMoreSearchResults.bind(this, tags));
		this.listenTo(TrackingActions.trackQuickEntrySearch, (searchTerm, searchIntentId) =>
			TrackingStoreImpl.trackSearch({ ...tags, searchIntentId }, searchTerm),
		);
		this.listenTo(TrackingActions.trackQuickEntryResults, (searchResults, searchIntentId) =>
			this.trackQuickEntryResults(searchResults, searchIntentId),
		);
	}

	trackQuickEntryResults(searchResults: SearchResults, searchIntentId: string) {
		const ownBrandCount = searchResults.filter((it) => it.ownBrand).length;
		const buyableCount = searchResults.filter((it) => it.buyable).length;
		trackStructured("quickEntry.search.results", {
			tags: {
				searchIntentId,
				resultCount: JSON.stringify(searchResults.length),
				buyableCount: JSON.stringify(buyableCount),
				ownBrandCount: JSON.stringify(ownBrandCount),
			},
		});
		const variantIds = new Set(searchResults.map((it) => it.variantId));
		searchResults.forEach((it: SearchResultEntry) => {
			trackStructured("quickEntry.search.result", {
				tags: {
					searchIntentId,
					rank: JSON.stringify(it.rank),
					variantId: it.variantId,
					bundleId: it.bundleId,
					hasFollowUp: JSON.stringify(it.hasFollowUp),
					predecessorDisplayed: JSON.stringify(variantIds.has(it.predecessorId || "")),
					buyable: JSON.stringify(it.buyable),
					ownBrand: it.ownBrand ? it.ownBrand : JSON.stringify(null),
				},
			});
		});
	}

	trackCartRecommendations() {
		this.listenTo(CartRecommendationActions.addItem, (bundleId, articleData, predictionId, cartId, origin) => {
			const { trackingId, ownBrand } = articleData || {};
			trackStructured("cart.recommendation.add", {
				tags: {
					predictionId,
					bundleId,
					cartId,
					origin,
					trackingId: trackingId || "",
					ownBrand: ownBrand || "",
					ownBrandCount: ownBrand ? 1 : 0,
				},
			});
		});
		this.listenTo(CartRecommendationActions.closeRecommendation, (predictionId, origin) => {
			trackStructured("cart.recommendation.close", {
				tags: { ...this.getCartTrackingContext(), origin, predictionId },
			});
		});
		this.listenTo(CartRecommendationActions.removeItem, (bundleId, articleData, predictionId, cartId, origin) => {
			const { trackingId } = articleData || {};
			trackStructured("cart.recommendation.downvote", {
				tags: {
					predictionId,
					bundleId,
					cartId,
					origin,
					trackingId: trackingId || "",
				},
			});
		});
		this.listenTo(
			CartRecommendationActions.onRecommendationShown,
			(cartId: string, recommendation: PredictionWithArticleData | null | undefined, origin: string) => {
				if (!recommendation) return;
				trackStructured("cart.recommendation.show", {
					tags: {
						cartId,
						origin,
						predictionId: recommendation.predictionId,
						bundleIds: recommendation.items.map((it) => it[0]).join(","),
						trackingIds: recommendation.items
							.filter((it) => !!it)
							.map((it) => it[1] && it[1].trackingId)
							.join(","),
					},
				});
			},
		);
	}

	trackSideCaddy() {
		this.listenTo(SideCaddyActions.trackGoToCart, () => {
			trackStructured("sideCaddy.goToCart", {
				tags: { ...this.getCartTrackingContext() },
			});
		});
		this.listenTo(SideCaddyActions.trackEnabled, (isEnabled: boolean | null | undefined) => {
			trackStructured("sideCaddy.enabled", {
				tags: {
					isEnabled: isEnabled === true ? "true" : isEnabled === false ? "false" : null,
					...this.getCartTrackingContext(),
				},
			});
		});
	}

	getAnonCartTrackingContext() {
		const cart = this._anonCartStore.getCart();

		return { ...this._anonCartStore.getCartContext(), cartId: cart ? cart.cartId : undefined };
	}

	getCustomerCartTrackingContext() {
		const cart = this._customerCartStore.getCart();

		return { ...this._customerCartContextStore.getState(), cartId: cart ? cart.cartId : null };
	}

	getCartTrackingContext() {
		const context = this._anonCartStore.isAnonymous()
			? this.getAnonCartTrackingContext()
			: this.getCustomerCartTrackingContext();
		// legalize the context, by removing sensitive data.
		const { cartId, storeId, locale, country } = context;
		return {
			cartId: cartId,
			storeId,
			locale,
			country,
		};
	}

	getCheckoutTrackingContext(includeSubHeaders = false) {
		const checkoutData = this._checkoutAggregatorStore.getCheckoutData();

		if (!checkoutData) return null;
		const tags = {
			checkoutId: checkoutData.id,
			cartId: checkoutData.context.cartId,
			bundleIds: Object.keys(checkoutData.items || {}).join(","),
		};
		return includeSubHeaders ? { ...tags, subHeaders: Object.keys(checkoutData.groupHeaders).join(",") } : tags;
	}

	static trackOrderEntryOpen() {
		trackStructured("orderEntry.open");
	}

	static trackOrderEntryClose() {
		trackStructured("orderEntry.close");
	}

	static trackFOECartCreated(cartId: string | null | undefined, customerId: string | null | undefined) {
		const hashedCustomerId = customerId ? new sha256().update(customerId).digest("hex") : "";
		trackStructured("orderEntry.cartCreated", {
			tags: {
				cartId: cartId,
				customerId: hashedCustomerId,
			},
		});
	}

	static trackFOECartSubmitted(cartId: string | null | undefined, customerId: string) {
		trackStructured("orderEntry.cartSubmitted", {
			tags: {
				cartId: cartId,
				customerId: TrackingStoreImpl.hashedValue(customerId),
			},
		});
	}

	static trackCreateCartPageCustomerSelected() {
		trackStructured("orderEntry.customerSelected");
	}

	static trackCartsPageCustomerSelected(params: Record<string, any>, searchOptions: Array<Record<string, any>>) {
		if (searchOptions) {
			trackStructured("orderEntry.search", {
				tags: {
					searchOptions: JSON.stringify(searchOptions.map((it) => it.value)),
				},
			});
		}
	}

	static trackDeliveryDate(date: Date, info: ValidDeliveryDate, source: "checkout" | "cart") {
		const { isCustomerSlot, isAlternative, validUntil, leadTimeCompliantUntil, deliveryTimes } = info;
		trackStructured(`${source}.deliveryDate.set`, {
			tags: {
				date: formatLocalDate(date, "yyyy-MM-dd"),
				offset: getTimezoneOffset(date) * 60,
				isCustomerSlot: isCustomerSlot.toString(),
				hasTimeSlots: (!!deliveryTimes.length).toString(),
				isAlternative: isAlternative.toString(),
				validUntil,
				leadTimeCompliantUntil,
			},
		});
	}

	static trackPickupDate(date: Date, info: ValidPickupDate, timeslot: Timeslot | null | undefined) {
		const { validUntil, leadTimeCompliantUntil } = info;
		trackStructured("checkout.pickupDate.set", {
			tags: {
				date: formatLocalDate(date, "yyyy-MM-dd"),
				offset: getTimezoneOffset(date) * 60,
				validUntil,
				leadTimeCompliantUntil,
				from: timeslot ? formatLocalTime(timeslot.fromUtc) : "",
				until: timeslot ? formatLocalTime(timeslot.untilUtc) : "",
			},
		});
	}

	static getPaymentName(payment: PaymentType): string {
		return payment.paymentType ? `${payment.paymentAllowance}-${payment.paymentType}` : payment.paymentAllowance;
	}

	getPaymentMethodName(payment: PaymentId): string {
		return payment.type ? `${payment.allowance}-${payment.type}` : payment.allowance;
	}

	trackPaymentMethodChange(isMainPayment: boolean, paymentSelection: PaymentMethodSelectionDetails) {
		const paymentMethod = this.getPaymentMethodName(paymentSelection.paymentId);
		// Keep the legacy naming in the event name for convenience in the tracking queries.
		const eventNameAddition = isMainPayment ? "primary" : "secondary";
		trackStructured(`checkout.payment.changed.${eventNameAddition}`, {
			tags: {
				paymentMethod,
				...this.getCheckoutTrackingContext(),
			},
		});
	}

	trackUnavailablePaymentShown(payment: PaymentType, unavailabilityReason: UnavailabilityReason) {
		const paymentType = payment.paymentType;
		const paymentAllowance = payment.paymentAllowance;
		const paymentMethod = paymentType ? `${paymentAllowance}-${paymentType}` : paymentAllowance;
		trackStructured(`checkout.unavailablePayment.displayed`, {
			tags: {
				paymentMethod,
				reasonType: unavailabilityReason.reasonType,
				totalBelowThreshold: unavailabilityReason.totalBelowThreshold || "",
				threshold: unavailabilityReason.threshold || "",
				...this.getCheckoutTrackingContext(),
			},
		});
	}

	trackUnavailableMetroCreditShown(blockingReason: string) {
		trackStructured(`checkout.unavailablePayment.displayed`, {
			tags: {
				paymentMethod: TrackingStoreImpl.getPaymentName(getMetroCredit()),
				blockingReason: blockingReason || "",
				...this.getCheckoutTrackingContext(),
			},
		});
	}

	trackShowUnavailabilityDialogMetroCreditBlocked() {
		trackStructured(`checkout.unavailablePayment.showDialog`, {
			tags: {
				paymentMethod: TrackingStoreImpl.getPaymentName(getMetroCredit()),
				...this.getCheckoutTrackingContext(),
			},
		});
	}

	trackCancelUnavailabilityDialog(payment: PaymentType) {
		trackStructured(`checkout.unavailablePayment.cancelDialog`, {
			tags: {
				paymentMethod: TrackingStoreImpl.getPaymentName(payment),
				...this.getCheckoutTrackingContext(),
			},
		});
	}

	trackCartErrors(errorState: ErrorState) {
		trackStructured(`cart.errors`, {
			tags: {
				cartErrorCount: JSON.stringify(errorState.cartErrors.size),
				itemErrorCount: JSON.stringify(errorState.itemErrors.size),
				...this.getCartTrackingContext(),
			},
		});
	}

	trackCartPrinted() {
		trackStructured(`cart.header`, {
			tags: { ...this.getCartTrackingContext() },
		});
	}

	trackCartSaved() {
		trackStructured(`cart.save`, {
			tags: { ...this.getCartTrackingContext() },
		});
	}

	trackCartDeleted(cartId: string | null | undefined) {
		trackStructured(`cart.delete`, {
			tags: { ...this.getCartTrackingContext(), cartId },
		});
	}

	trackCartFilterChanged(term: string, filterIntentId: string) {
		const cart = this._customerCartStore.getCart();

		if (!cart) {
			return;
		}

		const cartItems = Object.values(cart.items);

		const filteredItems = filterItems(term, cartItems);
		trackStructured(`cart.header.filter`, {
			tags: {
				term: term,
				filterIntentId: filterIntentId,
				numberOfItems: cartItems.length,
				numberOfFilteredItems: filteredItems.length,
				...this.getCartTrackingContext(),
			},
		});
	}

	trackSortCriterionChanged(criterion: string) {
		trackStructured(`cart.header.sort`, {
			tags: {
				crit: criterion,
				...this.getCartTrackingContext(),
			},
		});
	}

	trackItemAdded(tags: Record<string, any>, { bundleId, comment, articleData }: AddItemParams) {
		trackStructured(`cart.item.add`, {
			tags: {
				bettyBundleId: bundleId,
				withComment: String(!!comment),
				trackingId: articleData && articleData.trackingId,
				ownBrand: articleData && articleData.ownBrand,
				ownBrandCount: articleData && articleData.ownBrand ? 1 : 0,
				...this.getCartTrackingContext(),
				...tags,
			},
		});
	}

	trackItemQuantitySet(bundleId: string, quantity: number, source?: string | null) {
		const cart = this._anonCartStore.isAnonymous()
			? this._anonCartStore.getCart()
			: this._customerCartStore.getCart();

		if (cart && cart.items && cart.items[bundleId] && cart.items[bundleId].prices) {
			const appliedDnrs = cart.items[bundleId]?.prices?.appliedDnrs || [];
			const unappliedDnrs = cart.items[bundleId]?.prices?.unappliedDnrs || [];
			trackStructured(`cart.item.quantity`, {
				tags: {
					bettyBundleId: bundleId,
					quantity: quantity.toString(),
					source: source || "cart",
					activeDnrLabels: transformDnrInfoArrayToConcattedString(appliedDnrs),
					inactiveDnrLabels: transformDnrInfoArrayToConcattedString(unappliedDnrs),
					...this.getCartTrackingContext(),
				},
			});
		}
	}

	trackItemServiceOptionSet(bundleId: string, serviceOptionId: string, selected: boolean) {
		trackStructured(`cart.item.serviceOption`, {
			tags: {
				bettyBundleId: bundleId,
				serviceOptionId: serviceOptionId,
				selected: selected ? "true" : "false",
				...this.getCartTrackingContext(),
			},
		});
	}

	trackItemRemoved(bundleId: string, source?: string | null) {
		trackStructured(`cart.item.delete`, {
			tags: {
				bettyBundleId: bundleId,
				source: source || "cart",
				...this.getCartTrackingContext(),
			},
		});
	}

	trackItemsRemoved(bundleIds: string[]) {
		trackStructured(`cart.items.delete`, {
			tags: {
				bettyBundleIds: bundleIds.join(","),
				numberOfItems: bundleIds.length,
				source: "cart_all-items-with-errors",
				...this.getCartTrackingContext(),
			},
		});
	}

	trackManualPriceSet(bundleId: string, newPrice: number, reset: boolean, isBbd: boolean) {
		trackStructured(`cart.item.manualPrice`, {
			tags: {
				bettyBundleId: bundleId,
				newPrice: newPrice?.toString(),
				isBbd: isBbd ? "true" : "false",
				reset: reset ? "true" : "false",
				...this.getCartTrackingContext(),
			},
		});
	}

	trackCheckoutCart(oneClickSubmit: boolean, cartId: string | null | undefined) {
		trackStructured(`cart.checkout.started`, {
			tags: {
				...this.getCartTrackingContext(),
				cartId: cartId || "",
				oneClickSubmit: oneClickSubmit ? "true" : "false",
			},
		});
	}

	trackUploadModalShow() {
		trackStructured(`cart.csv.show`, {
			tags: { ...this.getCartTrackingContext() },
		});
	}

	trackUploadModalClear() {
		trackStructured(`cart.csv.clear`, {
			tags: { ...this.getCartTrackingContext() },
		});
	}

	trackUploadModalAddToCart(itemEntries: ItemEntry[]) {
		const validItems = itemEntries.filter((it) => it.isValid);
		const invalidItems = itemEntries.filter((it) => !it.isValid);
		const bundleIds = validItems.map((it) => it.bundleId).join(",");
		const trackingIds = validItems.map((it) => it.trackingId).join(",");
		trackStructured(`cart.csv.addToCart`, {
			tags: {
				numberItemsAdded: validItems.length,
				numberInvalidLines: invalidItems.length,
				numberNotFoundItems: invalidItems.filter((it) => !it.bundleId).length,
				numberAlreadyInCartItems: invalidItems.filter((it) => it.isInCart).length,
				bundleIds,
				trackingIds,
				...this.getCartTrackingContext(),
				ownBrandCount: validItems.filter((it) => it.ownBrand).length,
			},
		});
	}

	trackRestockCart(restockCartId: string, cartId: string, cartVersion: number, origin: string) {
		trackStructured(`cart.restock.request`, {
			tags: { ...this.getCartTrackingContext(), cartId, cartVersion, restockCartId, origin },
		});
	}

	trackResetCart(cartId: string | null | undefined) {
		trackStructured(`cart.context.remove`, {
			tags: { ...this.getCartTrackingContext(), cartId },
		});
	}

	trackLoadSavedCart(savedCartId: string | null | undefined) {
		trackStructured(`savedcart.load`, {
			tags: { ...this.getCartTrackingContext(), savedCartId },
		});
	}

	trackDeleteSavedCart(savedCartId: string | null | undefined, source?: string | null) {
		trackStructured(`savedcart.delete`, {
			tags: { ...this.getCartTrackingContext(), savedCartId, source: source || null },
		});
	}

	trackEditSavedCart(savedCartId: string | null | undefined) {
		trackStructured(`savedcart.edit`, {
			tags: { ...this.getCartTrackingContext(), savedCartId },
		});
	}

	trackReplaceArticle(originArticleId: string, replacementId: string) {
		trackStructured(`cart.article.replace`, {
			tags: { ...this.getCartTrackingContext(), originArticleId, replacementId },
		});
	}

	trackUploadCsvFile() {
		trackStructured(`cart.csv.fileUpload`, {
			tags: { ...this.getCartTrackingContext() },
		});
	}

	trackPasteCsv() {
		trackStructured(`cart.csv.paste`, {
			tags: { ...this.getCartTrackingContext() },
		});
	}

	trackDeleteCsvItem(bundleId: string) {
		trackStructured(`cart.csv.itemDelete`, {
			tags: {
				bettyBundleId: bundleId,
				...this.getCartTrackingContext(),
			},
		});
	}

	trackCsvCommentChange(bundleId: string) {
		trackStructured(`cart.csv.changeComment`, {
			tags: {
				bettyBundleId: bundleId,
				...this.getCartTrackingContext(),
			},
		});
	}

	trackCsvQuantityChange(bundleId: string, quantity: number) {
		trackStructured(`cart.csv.changeQuantity`, {
			tags: {
				bettyBundleId: bundleId,
				quantity: quantity,
				...this.getCartTrackingContext(),
			},
		});
	}

	trackSurveyStart(checkoutId: string | null | undefined, source: string, surveyId: string) {
		trackStructured("checkout.survey.start", {
			eventGroupId: surveyId,
			tags: { ...this.getCheckoutTrackingContext(), checkoutId: checkoutId || "", source },
		});
	}

	trackRatingClick(
		checkoutId: string | null | undefined,
		customerId: string | null | undefined,
		source: string | null | undefined,
		ratingScore: number,
		surveyId: string,
	) {
		trackStructured("checkout.survey.rating", {
			eventGroupId: surveyId,
			tags: {
				...this.getCheckoutTrackingContext(),
				checkoutId: checkoutId || "",
				source,
				ratingScore: ratingScore.toString(),
			},
		});
	}

	trackSurveySubmit(
		checkoutId: string | null | undefined,
		trackingData: SurveyTrackingData | null | undefined,
		source: string | null | undefined,
		selectedReasons: Array<string> | null | undefined,
		comment: string | null | undefined,
		rating: number | null | undefined,
		surveyId: string,
		customerId: string | null | undefined,
	) {
		trackStructured("checkout.survey.submit", {
			eventGroupId: surveyId,
			tags: {
				...this.getCheckoutTrackingContext(),
				checkoutId: checkoutId || "",
				source,
				comment: comment || "",
				customerId: customerId,
			},
		});

		if (selectedReasons) {
			selectedReasons.forEach((reason) =>
				trackStructured("checkout.survey.reason", {
					eventGroupId: surveyId,
					tags: { ...this.getCheckoutTrackingContext(), source, reason },
				}),
			);
		}
	}

	static trackSingleInvoiceSet(singleInvoiceSet: boolean) {
		trackStructured(`order.options.singleInvoice`, {
			tags: {
				singleInvoiceSet: singleInvoiceSet.toString(),
			},
		});
	}

	trackItemCommentSet(bundleId: string) {
		trackStructured(`cart.item.comment`, {
			tags: {
				bettyBundleId: bundleId,
				...this.getCartTrackingContext(),
			},
		});
	}

	static trackSearch(tags: Record<string, any>, searchTerm: string) {
		if (!searchTerm) {
			return;
		}

		trackStructured(`quickEntry.search`, {
			tags: {
				search: searchTerm,
				...tags,
			},
		});
	}

	static trackShowMoreSearchResults(tags: Record<string, any>, searchTerm: string, searchIntentId: string) {
		trackStructured(`quickEntry.more`, {
			tags: {
				search: searchTerm,
				searchIntentId,
				...tags,
			},
		});
	}

	trackCheckoutStep(page: string, checkoutData: CheckoutData | null | undefined) {
		this.trackCheckoutPages(page, checkoutData, "summaryButton");
	}

	trackCheckoutSkipStep(step: string) {
		trackStructured("checkout.skipStep", {
			tags: {
				step,
				...this.getCheckoutTrackingContext(),
			},
		});
	}

	trackCheckoutNavigation(page: string, checkoutData: CheckoutData | null | undefined) {
		this.trackCheckoutPages(page, checkoutData, "tabBar");
	}

	trackCheckoutPages(page: string, checkoutData: CheckoutData | null | undefined, source: string) {
		if (!checkoutData) {
			return;
		}

		const cartId = checkoutData.context.cartId;
		const checkoutId = checkoutData.id;
		let hasNonFsd = false;
		let hasFsd = false;

		for (const key in checkoutData.groupHeaders) {
			if (checkoutData.groupHeaders[key] && checkoutData.groupHeaders[key]?.fulfillmentType !== "FSD")
				hasNonFsd = true;
			else if (checkoutData.groupHeaders[key] && checkoutData.groupHeaders[key]?.fulfillmentType === "FSD")
				hasFsd = true;
		}

		const orderType = hasFsd ? (hasNonFsd ? "mixed" : "fsd") : "non-fsd";
		const checkoutTrackingObject = TrackingStoreGAUtils.getGATrackingCheckoutObject(checkoutData, page);

		if (!checkoutTrackingObject) {
			return;
		}

		trackStructuredEvent(
			`checkout.page.${page.toLowerCase()}`,
			{
				tags: { ...this.getCheckoutTrackingContext(), cartId, checkoutId, orderType, source },
			},
			checkoutTrackingObject,
		);
	}

	trackCheckoutCreated(checkoutData: CheckoutData | null | undefined, oneClickSubmit: boolean) {
		const oneClickSubmitString = oneClickSubmit ? "true" : "false";

		if (!checkoutData) {
			trackStructured(`checkout.created.checkoutInfoMissing`, {
				tags: { ...this.getCheckoutTrackingContext(), oneClickSubmit: oneClickSubmitString },
			});
			return;
		}

		const { items, context, id: checkoutId } = checkoutData;
		trackStructured(`checkout.created`, {
			tags: {
				...this.getCheckoutTrackingContext(true),
				cartId: context.cartId,
				checkoutId,
				oneClickSubmit: oneClickSubmitString,
			},
		});

		for (const itemId in items) {
			const item = items[itemId];
			const { prices } = item;
			if (!item || !prices) continue;
			const { freeProducts } = prices;
			const commonItemEventTags = {
				...this.getCheckoutTrackingContext(),
				checkoutId,
				itemId: item.bundleId,
				quantity: item.quantity.toFixed(2),
				oneClickSubmit: oneClickSubmitString,
			};

			if (freeProducts) {
				trackStructured("checkout.itemWithFreeProducts", {
					tags: { ...commonItemEventTags, freeProductCount: freeProducts.length },
					metric: freeProducts.length,
				});
			}
		}
	}

	sourcesByPath = {
		"/cart": "cart",
		"/order-entry/cart": "orderentry",
		[CheckoutSteps.Summary.url]: "summary",
	};

	trackSubmitSuccess(tags: Record<string, any>, checkoutData: CheckoutData) {
		const endPath = window.location.pathname.replace(/(\/(?:shop|backend)(?:\/checkout)?)/, "");
		const source = this.sourcesByPath[endPath] || endPath;
		const submitTags = getSubmitSuccessTrackingTags(checkoutData, source);
		const structuredData = {
			tags: { ...this.getCheckoutTrackingContext(), ...tags, ...submitTags },
			metric: submitTags.articleCount,
		};
		const ga360PurchaseEvent = TrackingStoreGAUtils.getGATrackingPurchaseObject(checkoutData);

		if (!ga360PurchaseEvent) {
			return;
		}

		// TJ: Cp. the comment at the "order.submit.success.ga360" event. The GA360 event is still
		//     passed here for backwards compatibility and should be passed here in the future.
		trackStructuredEvent("order.submit.success", structuredData, ga360PurchaseEvent);
		// TJ: We need to pass the GA360 event in a separate tracking event, since whitelisting
		//     the existing "order.submit.success" event in the uidispatcher's GTM consumer
		//     breaks due to an implicit dependency on "order.submit.success" _not_ being whitelisted.
		//      Once CI fixed that problem, we should whitelist "order.submit.success" there again
		//     and remove this additional tracking call.
		trackStructuredEvent("order.submit.success.ga360", structuredData, ga360PurchaseEvent);
	}

	trackSubmitCheckoutAttempt(oneClickSubmit: boolean) {
		trackStructured(`order.submit.started`, {
			tags: { ...this.getCheckoutTrackingContext(), oneClickSubmit: oneClickSubmit ? "true" : "false" },
		});
	}

	trackPaymentPreauthorization(requestData: PaymentPreauthorizationRequestData, paymentType: PaymentType) {
		trackStructured("checkout.payment.preauthorize", {
			tags: {
				...this.getCheckoutTrackingContext(),
				paymentType: paymentType.paymentType || "",
				savesPaymentData: requestData.multiUse ? "true" : "false",
			},
		});
	}

	static trackCreditCheck(event: string, retryCount?: number, eventGroupId?: string) {
		trackStructured(event, {
			eventGroupId,
			metric: retryCount,
		});
	}

	trackCartMerged(anonCart: AnonCart, targetCartId: string) {
		trackStructured("anonCart.merged", {
			tags: { ...this.getAnonCartTrackingContext(), cartId: targetCartId, anonCartId: anonCart.cartId },
		});
		const anonCartItems = new Set(Object.keys(anonCart.items));
		trackStructured("anonCart.merged.items", {
			tags: { ...this.getAnonCartTrackingContext(), cartId: targetCartId, anonCartId: anonCart.cartId },
			metric: anonCartItems.size,
		});
	}

	static trackMovShown(
		cartId: string | null | undefined,
		feeAmount: Money | null | undefined,
		minOrderValue: Money | null | undefined,
	) {
		trackStructured("cart.warnings.mov", {
			tags: {
				cartId: cartId || "",
				feeAmount: feeAmount ? feeAmount.amount.toString() + feeAmount.currency : "",
				minOrderValue: minOrderValue ? minOrderValue.amount.toString() + minOrderValue.currency : "",
			},
		});
	}

	static createTrackingIdsString(articles: ArticleData[]): string {
		return articles.map((article) => article.trackingId).join(",");
	}

	static createOwnBrandCount(articles: ArticleData[]): number {
		return articles.filter((article) => article.ownBrand).length;
	}

	static trackCartRestockedSuccess(cartId: string | null | undefined, bundleIds: string[], articles: ArticleData[]) {
		trackStructured("cart.restock.success", {
			tags: {
				cartId: cartId || "",
				bundleIds: bundleIds.join(),
				trackingIds: TrackingStoreImpl.createTrackingIdsString(articles),
				ownBrandCount: TrackingStoreImpl.createOwnBrandCount(articles),
			},
		});
	}

	static trackSavedCartLoadedSuccess(
		cartId: string | null | undefined,
		bundleIds: string[],
		articles: ArticleData[],
	) {
		trackStructured("savedcart.load.success", {
			tags: {
				cartId: cartId || "",
				bundleIds: bundleIds.join(),
				trackingIds: TrackingStoreImpl.createTrackingIdsString(articles),
				ownBrandCount: TrackingStoreImpl.createOwnBrandCount(articles),
			},
		});
	}

	static trackNotificationShown(data: IntlNotification) {
		trackStructured("client.notification", {
			tags: {
				type: data.type,
				text: data.text,
				id: data.id,
			},
		});
	}

	static trackGuerillaFeature(slug: string) {
		trackStructured("guerilla.feature.click", {
			tags: {
				featureId: slug,
			},
		});
	}

	trackComponent(eventType: string, tags: Record<string, any>) {
		trackStructured(`ui.${eventType}`, {
			tags: { ...tags, ...this.getCustomerCartTrackingContext(), ...this.getCheckoutTrackingContext() },
		});
	}

	trackAbandonedCartEmailClick(abandonedCartId: string) {
		trackStructured("abandonedCart.fromEmail", {
			tags: { ...this.getCustomerCartTrackingContext(), ...this.getCheckoutTrackingContext(), abandonedCartId },
		});
	}

	trackAbandonedCartEmailCartMismatch(abandonedCartId: string, currentCartId: string) {
		trackStructured("abandonedCart.fromEmail.mismatch", {
			tags: {
				...this.getCustomerCartTrackingContext(),
				...this.getCheckoutTrackingContext(),
				abandonedCartId,
				currentCartId,
			},
		});
	}
}
export const TrackingStore = new TrackingStoreImpl(
	CustomerCartItemStore,
	AnonCartNotificationStore,
	CustomerCartNotificationStore,
	AnonCartStore,
	CustomerCartStore,
	CustomerCartContextStore,
	CheckoutAggregatorStore,
);
